import { useState } from 'react';
import { Box, Button, LinearProgress, MenuItem, TextField, Typography } from '@mui/material';
import { percentUsed, seatCandidates, seatSummary } from '../utils';
import Avatar from './Avatar';
import { barTrackSx, BORDER, bodySx, buttonSx, captionSx, dangerButtonSx, DANGER, eyebrowSx, headingSx, INK, inputSx, SOFT } from './styles';

// Seat holders for one product, with Revoke per holder and a Grant row.
// readOnly (QueenBee) lists holders only.
export default function SeatsPanel({ product, seats, members, readOnly, isGrantBusy, isRevokeBusy, onGrant, onRevoke }) {
  const [selected, setSelected] = useState('');
  const holders = seats?.holders || [];
  const { heading, full, caption } = seatSummary(seats, readOnly);
  const candidates = seatCandidates(members, holders);
  const limit = seats?.limit;
  // The contract includes no seats: nothing to grant, so say so instead of
  // showing controls that can never be used.
  const noSeats = !readOnly && limit === 0 && holders.length === 0;
  const pct = !readOnly && limit > 0 ? percentUsed(seats?.used ?? holders.length, limit) : null;

  return (
    <Box data-testid={`seats-${product}`}>
      <Typography sx={{ ...eyebrowSx, mb: 0.75 }}>Seats</Typography>
      <Typography sx={{ ...headingSx, mb: 0.75 }}>{heading}</Typography>
      {pct !== null && (
        <LinearProgress
          variant="determinate"
          value={pct}
          aria-label={`${product} seats used`}
          sx={{ ...barTrackSx, height: 6, mb: 1.25, '& .MuiLinearProgress-bar': { borderRadius: '99px', backgroundColor: pct >= 100 ? DANGER : 'primary.main' } }}
        />
      )}
      {readOnly && (
        <Typography sx={{ ...captionSx, mb: 1.25 }}>Every active member of the organisation holds a seat.</Typography>
      )}

      {noSeats ? null : holders.length === 0 ? (
        <Box sx={{ border: `1px dashed ${BORDER}`, borderRadius: '10px', p: 2, textAlign: 'center' }}>
          <Typography sx={captionSx}>No one holds a seat yet.</Typography>
        </Box>
      ) : (
        <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, border: `1px solid ${BORDER}`, borderRadius: '10px', maxHeight: 320, overflowY: 'auto' }}>
          {holders.map((h) => (
            <Box
              component="li"
              key={h.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1.5,
                px: 1.5,
                py: 1,
                borderBottom: `1px solid ${BORDER}`,
                '&:last-of-type': { borderBottom: 0 },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                <Avatar name={h.name} email={h.email} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ ...bodySx, fontWeight: 500, color: INK }}>{h.name}</Typography>
                  <Typography sx={{ ...captionSx, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h.email}</Typography>
                </Box>
              </Box>
              {!readOnly && (
                <Button
                  size="small"
                  disabled={isRevokeBusy(h.id)}
                  onClick={() => onRevoke(h)}
                  sx={{ ...dangerButtonSx, minHeight: { xs: 36, sm: 30 }, px: 1.5, flexShrink: 0 }}
                >
                  Revoke
                </Button>
              )}
            </Box>
          ))}
        </Box>
      )}

      {noSeats && (
        <Box sx={{ mt: 1.5, p: 1.5, borderRadius: '10px', backgroundColor: SOFT }}>
          <Typography sx={captionSx}>{caption}</Typography>
        </Box>
      )}

      {!readOnly && !noSeats && (
        <Box sx={{ mt: 1.5, p: 1.5, borderRadius: '10px', backgroundColor: SOFT }}>
          <Typography sx={{ ...bodySx, fontWeight: 600, color: INK, mb: 1 }}>Give a seat</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <TextField
              select
              id={`seat-member-${product}`}
              size="small"
              label="Member"
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
              sx={{ ...inputSx, flex: 1, minWidth: { xs: '100%', sm: 200 } }}
              disabled={candidates.length === 0 || full}
            >
              {candidates.map((m) => (
                <MenuItem key={m.id} value={String(m.id)} sx={bodySx}>
                  {m.name} ({m.email})
                </MenuItem>
              ))}
            </TextField>
            <Button
              variant="contained"
              disableElevation
              disabled={isGrantBusy || !selected || full}
              onClick={() => {
                const member = candidates.find((m) => String(m.id) === selected);
                if (member) onGrant(member, () => setSelected(''));
              }}
              sx={{ ...buttonSx, width: { xs: '100%', sm: 'auto' } }}
            >
              Grant seat
            </Button>
          </Box>
          {caption && <Typography sx={{ ...captionSx, mt: 1 }}>{caption}</Typography>}
          {!full && candidates.length === 0 && (
            <Typography sx={{ ...captionSx, mt: 1 }}>Every member already holds a seat.</Typography>
          )}
        </Box>
      )}
    </Box>
  );
}
